import React, { createContext, useEffect, useReducer } from 'react';
import { FC, ReactNode } from 'react';
import { Auth0Client } from '@auth0/auth0-spa-js';
import { User } from 'src/types/user';
import SplashScreen from 'src/components/SplashScreen';
import {
  auth0Config,
  sessionConfig,
  SESSION_LOGOUT_REASON_KEY,
  SESSION_LOGOUT_REASON_INACTIVITY,
  SESSION_LOGOUT_REASON_EXPIRED
} from 'src/config';
import history from 'src/utils/history';
import JwtDecode from 'jwt-decode';
import { IToken } from 'src/types/global';
import { readAuthErrorFromSearch, storeAuthError } from 'src/lib/authError';
import {
  clearActivity,
  idleTooLong,
  onSessionExpired,
  setTokenGetter,
  touchActivity
} from 'src/lib/session';

// Same options on every token call so they share one cache entry.
const TOKEN_OPTIONS = {
  domain: auth0Config.domain,
  redirectUri: auth0Config.redirect_uri,
  audience: auth0Config.audience,
  scope: 'openid'
};

const setLogoutReason = (reason: string) => {
  try {
    sessionStorage.setItem(SESSION_LOGOUT_REASON_KEY, reason);
  } catch (e) {
    // storage blocked; the logout still happens
  }
};

export const getRoles = () => {
  let role = 'cms_user';
  const token = sessionStorage.getItem('token');
  const decodedToken = JwtDecode<IToken>(token);

  if (
    decodedToken &&
    decodedToken['https://hasura.io/jwt/claims'] &&
    decodedToken['https://hasura.io/jwt/claims']['x-hasura-allowed-roles']
  ) {
    role =
      decodedToken['https://hasura.io/jwt/claims']['x-hasura-default-role'];
  }

  return role;
};

let auth0Client: Auth0Client | null = null;

interface AuthState {
  isInitialised: boolean;
  isAuthenticated: boolean;
  user: User | null;
}

export interface AuthContextValue extends AuthState {
  method: 'Auth0';
  loginWithPopup: (options?: any) => Promise<void>;
  logout: () => void;
}

interface AuthProviderProps {
  children: ReactNode;
}

type InitialiseAction = {
  type: 'INITIALISE';
  payload: {
    isAuthenticated: boolean;
    user: User | null;
  };
};

type LoginAction = {
  type: 'LOGIN';
  payload: {
    user: User;
  };
};

type LogoutAction = {
  type: 'LOGOUT';
};

type RegisterAction = {
  type: 'REGISTER';
};

type Action = InitialiseAction | LoginAction | LogoutAction | RegisterAction;

const initialAuthState: AuthState = {
  isAuthenticated: false,
  isInitialised: false,
  user: null
};

const reducer = (state: AuthState, action: Action): AuthState => {
  switch (action.type) {
    case 'INITIALISE': {
      const { isAuthenticated, user } = action.payload;

      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user
      };
    }
    case 'LOGIN': {
      const { user } = action.payload;

      console.log(user);

      return {
        ...state,
        isAuthenticated: true,
        user
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
        user: null
      };
    }
    default: {
      return { ...state };
    }
  }
};

const onRedirectCallback = (appState: any): void => {
  const url =
    appState && appState.targetUrl
      ? appState.targetUrl
      : window.location.pathname;
  history.push(url);
};

const AuthContext = createContext<AuthContextValue>({
  ...initialAuthState,
  method: 'Auth0',
  loginWithPopup: () => Promise.resolve(),
  logout: () => {}
});

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState);

  const loginWithPopup = async options => {
    await auth0Client.loginWithRedirect(options);

    const isAuthenticated = await auth0Client.isAuthenticated();

    if (isAuthenticated) {
      const user = await auth0Client.getUser();
      const token = await auth0Client.getTokenSilently(TOKEN_OPTIONS);

      // Here you should extract the complete user profile to make it available in your entire app.
      // The auth state only provides basic information.

      dispatch({
        type: 'LOGIN',
        payload: {
          user: {
            id: user.sub,
            avatar: user.picture,
            email: user.email,
            name: user.name,
            token,
            tier: 'Premium'
          }
        }
      });
    }
  };

  const logout = () => {
    // Clear the cached access token so it does not linger in sessionStorage
    // after sign-out (previously it survived until the tab was closed).
    sessionStorage.removeItem('token');
    clearActivity();
    setTokenGetter(null);
    // Staging and prod share one Auth0 application; without returnTo Auth0
    // sends everyone to its first Allowed Logout URL (prod).
    auth0Client.logout({ returnTo: window.location.origin });

    dispatch({
      type: 'LOGOUT'
    });
  };

  // A request found the session dead (refresh failed / token rejected): one
  // explanation on the login screen instead of a toast per failed request.
  useEffect(
    () =>
      onSessionExpired(() => {
        setLogoutReason(SESSION_LOGOUT_REASON_EXPIRED);
        logout();
      }),
    []
  );

  useEffect(() => {
    const initialise = async () => {
      // Read-only: park any error Auth0 sent back before the redirects to
      // /login strip the query string.
      storeAuthError(readAuthErrorFromSearch(window.location.search));

      try {
        auth0Client = new Auth0Client({
          redirect_uri: window.location.origin,
          ...auth0Config,
          // Keep the login across page reloads. The default in-memory cache
          // was lost on reload, and the fallback (a hidden Auth0 iframe) needs
          // third-party cookies, which Incognito/Safari/Brave block — so every
          // reload logged the user out.
          cacheLocation: 'localstorage',
          // Renew access tokens with rotating refresh tokens instead of that
          // iframe. Needs "Refresh Token Rotation" on the Auth0 application and
          // "Allow Offline Access" on the API; until then the SDK falls back to
          // the iframe exactly as before.
          useRefreshTokens: true
        });

        await auth0Client.checkSession();

        if (
          window.location.search.includes('code=') &&
          window.location.search.includes('state=')
        ) {
          const { appState } = await auth0Client.handleRedirectCallback();
          onRedirectCallback(appState);
        }

        const isAuthenticated = await auth0Client.isAuthenticated();

        // The login now survives reloads, so the 30-minute idle rule has to
        // survive them too: a stored login idle for longer is signed out.
        if (isAuthenticated && idleTooLong(sessionConfig.idleMs)) {
          setLogoutReason(SESSION_LOGOUT_REASON_INACTIVITY);
          sessionStorage.removeItem('token');
          clearActivity();
          auth0Client.logout({ returnTo: window.location.origin });
          return;
        }

        if (isAuthenticated) {
          const user = await auth0Client.getUser();
          const token = await auth0Client.getTokenSilently(TOKEN_OPTIONS);
          sessionStorage.setItem('token', token);
          touchActivity();
          // Every request asks for a token: the SDK returns the cached one or
          // refreshes it shortly before it expires. Kept in sessionStorage too
          // for the code that reads it directly (roles, calibration tests).
          setTokenGetter(async () => {
            const fresh = await auth0Client.getTokenSilently(TOKEN_OPTIONS);
            sessionStorage.setItem('token', fresh);
            return fresh;
          });

          // Here you should extract the complete user profile to make it available in your entire app.
          // The auth state only provides basic information.

          dispatch({
            type: 'INITIALISE',
            payload: {
              isAuthenticated,
              user: {
                id: user.sub,
                avatar: user.picture,
                email: user.email,
                name: user.name,
                token,
                tier: 'Premium'
              }
            }
          });
        } else {
          dispatch({
            type: 'INITIALISE',
            payload: {
              isAuthenticated,
              user: null
            }
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALISE',
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    };

    initialise();
  }, []);

  if (!state.isInitialised) {
    return <SplashScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'Auth0',
        loginWithPopup,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
